import { useEffect, useRef } from "react";
import { StepName } from "../capTypes";

const getCapStepSection = (stepName: StepName) =>
  document.querySelector<HTMLElement>(
    `[data-cap-step="${CSS.escape(stepName)}"]`
  );

const PROGRAMMATIC_SCROLL_LOCK_MS = 1200;

let programmaticScrollLockUntil = 0;
let programmaticScrollTimer: number | null = null;

const isProgrammaticScrollActive = () =>
  Date.now() < programmaticScrollLockUntil;

const beginProgrammaticScroll = () => {
  programmaticScrollLockUntil = Date.now() + PROGRAMMATIC_SCROLL_LOCK_MS;

  if (programmaticScrollTimer !== null) {
    window.clearTimeout(programmaticScrollTimer);
  }

  programmaticScrollTimer = window.setTimeout(() => {
    programmaticScrollLockUntil = 0;
    programmaticScrollTimer = null;
  }, PROGRAMMATIC_SCROLL_LOCK_MS);
};

const useCapScrollSpy = (
  stepNames: StepName[],
  currentStep: StepName,
  onStepChange: (step: StepName) => void,
  navOffset: number
) => {
  const currentStepRef = useRef(currentStep);
  const onStepChangeRef = useRef(onStepChange);

  useEffect(() => {
    currentStepRef.current = currentStep;
  }, [currentStep]);

  useEffect(() => {
    onStepChangeRef.current = onStepChange;
  }, [onStepChange]);

  useEffect(() => {
    if (stepNames.length === 0) {
      return;
    }

    const visibleSteps = new Map<Element, StepName>();

    const observer = new IntersectionObserver(
      (entries) => {
        if (isProgrammaticScrollActive()) {
          return;
        }

        entries.forEach((entry) => {
          const stepName = visibleSteps.get(entry.target);
          if (!stepName) {
            return;
          }

          if (entry.isIntersecting) {
            entry.target.setAttribute("data-cap-visible", "true");
          } else {
            entry.target.removeAttribute("data-cap-visible");
          }
        });

        const intersectingSections = stepNames
          .map((stepName) => {
            const section = getCapStepSection(stepName);
            if (section?.getAttribute("data-cap-visible") !== "true") {
              return null;
            }

            return {
              stepName,
              top: section.getBoundingClientRect().top,
            };
          })
          .filter(
            (section): section is { stepName: StepName; top: number } =>
              section !== null
          );

        const activeStep =
          intersectingSections.sort((a, b) => a.top - b.top)[0]?.stepName ??
          currentStepRef.current;

        if (activeStep !== currentStepRef.current) {
          onStepChangeRef.current(activeStep);
        }
      },
      {
        root: null,
        rootMargin: `-${navOffset}px 0px -55% 0px`,
        threshold: 0,
      }
    );

    stepNames.forEach((stepName) => {
      const section = getCapStepSection(stepName);
      if (section) {
        visibleSteps.set(section, stepName);
        observer.observe(section);
      }
    });

    return () => observer.disconnect();
  }, [navOffset, stepNames]);
};

const scrollToCapStep = (stepName: StepName, navOffset: number) => {
  const section = getCapStepSection(stepName);
  if (!section) {
    return;
  }

  beginProgrammaticScroll();

  const scroll = () => {
    const targetTop =
      section.getBoundingClientRect().top + window.scrollY - navOffset;

    window.scrollTo({
      top: Math.max(0, targetTop),
      behavior: "smooth",
    });
  };

  requestAnimationFrame(() => requestAnimationFrame(scroll));
};

export { scrollToCapStep, useCapScrollSpy };
